import React from 'react';
import { Col, Divider, Row } from 'antd';
import { isEqual, isNil } from 'lodash';

import {
  FullBOUserResponseModel,
  MemberHistoryDetailsModel,
} from 'api/adminApiClient';
import HistoryLog from '../../HistoryLog';
import HistoryField from '../../HistoryField';
import BaseAlert from 'components/wrappers/BaseAlert';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import { downloadFile } from 'utils/fileUtils';
import { getRecruiterPriceAgreementInfoFile } from 'redux/reducers/recruiterSlice';
import { useAppDispatch } from 'hooks/useAppDispatch';

import styles from '../../HistoryCard.module.less';

type Props = {
  userId?: string;
  historyDetails: MemberHistoryDetailsModel;
  historyId: string;
};

export default function PricingAgreementDetailsCard(props: Props) {
  const { historyDetails, userId, historyId } = props;
  const { oldData, newData } = historyDetails;
  const dispatch = useAppDispatch();
  const hasAgreementFileKeyChanged = !isEqual(
    oldData?.businessInfo?.priceAgreement?.supportingFiles || '',
    newData?.businessInfo?.priceAgreement?.supportingFiles || '',
  );
  const hasICPriceChanged = !isEqual(
    oldData?.businessInfo?.priceAgreement?.icPrice || 0,
    newData?.businessInfo?.priceAgreement?.icPrice || 0,
  );
  const hasW2InfoListChanged = !isEqual(
    oldData?.businessInfo?.priceAgreement?.w2InfoList || [],
    newData?.businessInfo?.priceAgreement?.w2InfoList || [],
  );

  const handleDownloadAgreement = async (
    fileKey?: string,
    fileName?: string,
  ) => {
    const result = await dispatch(
      getRecruiterPriceAgreementInfoFile({
        userId,
        fileKey,
        fileName,
        isAdminRole: true,
      }),
    );

    if (getRecruiterPriceAgreementInfoFile.fulfilled.match(result)) {
      try {
        await downloadFile({
          downloadUrl: result.payload.downloadUrl,
          fileName: result.payload.fileName,
        });
      } catch (e: any) {
        BaseAlert.error(e.message);
      }
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };
  const renderPricingAgreement = (
    recruiterProfile?: FullBOUserResponseModel,
  ) => {
    if (!recruiterProfile) return null;

    const { businessInfo } = recruiterProfile;
    const sectionName = 'pricing-agreement';
    const attachments = businessInfo?.priceAgreement?.supportingFiles;
    const w2InfoList = businessInfo?.priceAgreement?.w2InfoList;

    return (
      <>
        <Row gutter={[10, 10]}>
          <Col xs={24}>
            <div className={styles.cardTitle}>Pricing agreement</div>
          </Col>

          {hasW2InfoListChanged && (
            <>
              {!!w2InfoList?.length &&
                w2InfoList.map((set, index: number) => (
                  <React.Fragment key={`member-w2-${set.w2Price}-${index}`}>
                    <HistoryField
                      title="W2 price"
                      value={set.w2Price && `${set.w2Price.toFixed(2)}%`}
                      dataId={`${sectionName}-w2Price-field`}
                    />

                    <HistoryField
                      title="W2 states"
                      value={set.placementStates
                        ?.map((state) => state.name)
                        .join(', ')}
                      dataId={`${sectionName}-w2States-field`}
                    />
                    {/*<HistoryField
                      title="WC Codes"
                      value={set.workCodes?.map((code) => code).join(', ')}
                      dataId={`${sectionName}-workCodes-field`}
                    />*/}
                  </React.Fragment>
                ))}
            </>
          )}

          {hasICPriceChanged && (
            <HistoryField
              title="IC price"
              value={
                !isNil(businessInfo?.priceAgreement?.icPrice) &&
                // As icPrice is 0 by default it should
                // display 'No data' on first edit for old model
                !!businessInfo?.priceAgreement?.w2InfoList?.length
                  ? `${businessInfo?.priceAgreement?.icPrice?.toFixed(2)}%`
                  : undefined
              }
              dataId={`${sectionName}-icPrice-field`}
            />
          )}

          {hasAgreementFileKeyChanged && (
            <HistoryField
              title="Agreement"
              emptyText="No file"
              value={
                attachments?.length
                  ? attachments.map((file) => (
                      <div
                        className="link-imitation"
                        key={file.fileKey}
                        onClick={() =>
                          handleDownloadAgreement(file.fileKey, file.fileName)
                        }
                      >
                        {file?.fileName}
                      </div>
                    ))
                  : null
              }
              dataId={`${sectionName}-file-field`}
            />
          )}
        </Row>

        <Divider />
      </>
    );
  };

  return (
    <HistoryLog
      historyId={historyId}
      oldData={renderPricingAgreement(oldData)}
      newData={renderPricingAgreement(newData)}
    />
  );
}
